import { db } from "./firebaseConfig";
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, query, arrayUnion, where } from "firebase/firestore";

// ========================================
// Gruppen-Prefix System
// ========================================
let currentGroupSlug = localStorage.getItem("active_group_slug") || "ballkinder";

export const setActiveGroupSlug = (slug) => {
  const changed = currentGroupSlug !== slug;
  currentGroupSlug = slug;
  if (changed) invalidateAllCaches();
};

export const getActiveGroupSlug = () => currentGroupSlug;

// Collection mit Gruppen-Prefix
const col = (name) => collection(db, `${currentGroupSlug}__${name}`);
const docRef = (name, id) => doc(db, `${currentGroupSlug}__${name}`, id);

// ========================================
// Cache-Verwaltung
// ========================================
let memberCache = null;
let memberById = new Map();
let memberByName = new Map();
let schedulesCache = null;
let freikartenCache = null;
const cachedAttendance = new Map();
let attendanceCachePromise = null;
let checkInOutCache = null;
let checkInOutCacheTimestamp = 0;
const CHECK_IN_CACHE_TTL = 30 * 1000; // 30 seconds
let membersLoadPromise = null; // Prevents concurrent loads

const MEMBERS_CACHE_KEY = () => `members_cache_${currentGroupSlug}`;
const SCHEDULES_CACHE_KEY = () => `schedules_cache_${currentGroupSlug}`;
const FREIKARTEN_CACHE_KEY = () => `freikarten_cache_${currentGroupSlug}`;
const CACHE_TTL = 10 * 60 * 1000;
const SHORT_TTL = 3 * 60 * 1000;

const invalidateAllCaches = () => {
  memberCache = null;
  memberById = new Map();
  memberByName = new Map();
  schedulesCache = null;
  freikartenCache = null;
  cachedAttendance.clear();
  attendanceCachePromise = null;
  checkInOutCache = null;
  checkInOutCacheTimestamp = 0;
  membersLoadPromise = null;
  try {
    localStorage.removeItem(MEMBERS_CACHE_KEY());
    localStorage.removeItem(SCHEDULES_CACHE_KEY());
    localStorage.removeItem(FREIKARTEN_CACHE_KEY());
  } catch (e) {}
};

// ========================================
// Mitglieder (frueher "Ballkids")
// ========================================
const buildMaps = (data) => {
  memberById = new Map();
  memberByName = new Map();
  data.forEach((m) => {
    memberById.set(m.id, m);
    if (m.name) memberByName.set(m.name, m);
  });
};

const loadMembersIfNeeded = async () => {
  if (memberCache) return memberCache;

  // Prevent concurrent loads
  if (membersLoadPromise) return membersLoadPromise;

  membersLoadPromise = (async () => {
    try {
      const cached = localStorage.getItem(MEMBERS_CACHE_KEY());
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL && data?.length > 0) {
          memberCache = data;
          buildMaps(data);
          refreshMembersInBackground();
          return memberCache;
        }
      }
    } catch (e) {}

    return await refreshMembers();
  })();

  try {
    return await membersLoadPromise;
  } finally {
    membersLoadPromise = null;
  }
};

const refreshMembers = async () => {
  const snap = await getDocs(col("members"));
  memberCache = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  buildMaps(memberCache);

  try {
    const lightData = memberCache.map(({ id, name, email, phone, canNet, jobs }) => ({ id, name, email, phone, canNet, jobs }));
    localStorage.setItem(MEMBERS_CACHE_KEY(), JSON.stringify({ data: lightData, timestamp: Date.now() }));
  } catch (e) {}

  console.log(`${memberCache.length} Mitglieder von Firestore geladen und gecached`);
  return memberCache;
};

const refreshMembersInBackground = () => {
  refreshMembers().catch((e) => console.error("Hintergrund-Refresh fehlgeschlagen:", e));
};

export const invalidateMembersCache = () => {
  memberCache = null;
  memberById = new Map();
  memberByName = new Map();
  try { localStorage.removeItem(MEMBERS_CACHE_KEY()); } catch (e) {}
};

// Aliase fuer Abwaertskompatibilitaet
export const getBallkids = async () => await loadMembersIfNeeded();
export const getMembers = getBallkids;
export const getAllBallkids = getBallkids;
export const invalidateBallkidsCache = invalidateMembersCache;

export const getBallkidById = async (id) => {
  await loadMembersIfNeeded();
  return memberById.get(id) || null;
};
export const getMemberById = getBallkidById;

export const getBallkidNameById = async (id) => {
  await loadMembersIfNeeded();
  const member = memberById.get(id);
  return member?.name || "Unbekannt";
};

export const getBallkidIdByName = async (name) => {
  await loadMembersIfNeeded();
  const member = memberByName.get(name);
  return member?.id || null;
};

export const setBallkidCanNet = async (ballkidId, canNet) => {
  await updateDoc(docRef("members", ballkidId), { canNet: !!canNet });
  invalidateMembersCache();
};

// ========================================
// Einteilungen / Schedules
// ========================================
export const getAllBallkidSchedules = async () => {
  if (schedulesCache) return schedulesCache;

  try {
    const cached = localStorage.getItem(SCHEDULES_CACHE_KEY());
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < (2 * 60 * 1000) && data) {
        schedulesCache = data;
        refreshSchedulesInBackground();
        return schedulesCache;
      }
    }
  } catch (e) {}

  return await refreshSchedules();
};

const refreshSchedules = async () => {
  const snapshot = await getDocs(col("courtAssignments"));
  const scheduleMap = {};

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    // Document IDs can be "{day}_{court}" or "{day}_{court}_{crew}"
    const parts = docSnap.id.split("_");
    const day = parts[0];
    const court = parts[1];

    // Check for crew-keyed format (crewA, crewB, ...) inside document
    for (const crew of ["A", "B", "C", "D"]) {
      const crewKey = `crew${crew}`;
      if (data[crewKey]) {
        for (const memberId of data[crewKey]) {
          if (!scheduleMap[memberId]) scheduleMap[memberId] = [];
          scheduleMap[memberId].push({ day, court, crew });
        }
      }
    }

    // Also check for "ballkids" array format with crew in document ID
    // Document ID format: "{day}_{court}_{crewX}" e.g. "13. April_Center Court_crewA"
    if (data.ballkids && parts.length >= 3) {
      const crewLabel = parts[2].replace("crew", ""); // "crewA" -> "A"
      for (const memberId of data.ballkids) {
        if (!scheduleMap[memberId]) scheduleMap[memberId] = [];
        scheduleMap[memberId].push({ day, court, crew: crewLabel });
      }
    }
  }

  for (const id of Object.keys(scheduleMap)) {
    scheduleMap[id].sort((a, b) => a.day.localeCompare(b.day));
  }

  schedulesCache = scheduleMap;
  try {
    localStorage.setItem(SCHEDULES_CACHE_KEY(), JSON.stringify({ data: scheduleMap, timestamp: Date.now() }));
  } catch (e) {}

  return scheduleMap;
};

const refreshSchedulesInBackground = () => {
  refreshSchedules().catch(() => {});
};

export const getBallkidSchedule = async (ballkidId) => {
  const allSchedules = await getAllBallkidSchedules();
  return allSchedules[ballkidId] || [];
};

// ========================================
// Freikarten
// ========================================
export const getFreikarten = async () => {
  if (freikartenCache) return freikartenCache;

  try {
    const cached = localStorage.getItem(FREIKARTEN_CACHE_KEY());
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < SHORT_TTL && data?.length > 0) {
        freikartenCache = data;
        refreshFreikartenInBackground();
        return freikartenCache;
      }
    }
  } catch (e) {}

  return await refreshFreikarten();
};

const refreshFreikarten = async () => {
  const snapshot = await getDocs(col("freikarten"));
  freikartenCache = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  try {
    localStorage.setItem(FREIKARTEN_CACHE_KEY(), JSON.stringify({ data: freikartenCache, timestamp: Date.now() }));
  } catch (e) {}
  return freikartenCache;
};

const refreshFreikartenInBackground = () => {
  refreshFreikarten().catch(() => {});
};

// Frische Daten laden (Cache ignorieren)
export const getFreikartenFresh = async () => {
  invalidateFreikartenCache();
  return await refreshFreikarten();
};

export const getFreikartenForBallkid = async (ballkidId) => {
  const ref = docRef("freikarten", ballkidId);
  const snap = await getDoc(ref);
  return snap.exists()
    ? { id: snap.id, ...snap.data() }
    : { id: ballkidId, total: 0, days: {}, confirmed: {} };
};

const invalidateFreikartenCache = () => {
  freikartenCache = null;
  try { localStorage.removeItem(FREIKARTEN_CACHE_KEY()); } catch (e) {}
};

export const updateFreikarten = async (ballkidId, day, count, maxFreikarten = 4) => {
  invalidateFreikartenCache();
  const ref = docRef("freikarten", ballkidId);
  const snap = await getDoc(ref);

  const data = snap.exists()
    ? snap.data()
    : { total: 0, days: {}, confirmed: {} };

  const days = data.days || {};
  const totalFreikarten = Object.values(days).reduce((sum, val) => sum + val, 0) - (days[day] || 0) + count;
  if (totalFreikarten > maxFreikarten) throw new Error("Maximale Freikarten-Anzahl überschritten");

  await setDoc(ref, {
    days: { ...days, [day]: count },
    confirmed: data.confirmed || {},
    total: totalFreikarten,
  });
};

export const confirmFreikarten = async (ballkidId, day) => {
  invalidateFreikartenCache();
  const ref = docRef("freikarten", ballkidId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data();
    await setDoc(ref, {
      ...data,
      confirmed: { ...(data.confirmed || {}), [day]: true },
    });
  } else {
    await setDoc(ref, { confirmed: { [day]: true }, days: {}, total: 0 });
  }
};

// ========================================
// Anwesenheit / Attendance
// ========================================
export const getAttendance = async () => {
  const snapshot = await getDocs(col("attendance"));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const initAttendanceCache = () => {
  if (!attendanceCachePromise) {
    attendanceCachePromise = getDocs(col("attendance"))
      .then((snap) => {
        snap.forEach((d) => {
          cachedAttendance.set(d.id, d.data());
        });
        console.log(`📦 Attendance-Daten vorgeladen (${cachedAttendance.size})`);
      })
      .catch((error) => {
        console.error("Fehler beim Preloading der Attendance-Daten:", error);
        attendanceCachePromise = null;
      });
  }
  return attendanceCachePromise;
};

export const getAttendanceForBallkid = async (ballkidId) => {
  if (!ballkidId) return { availability: {} };
  await initAttendanceCache();

  if (cachedAttendance.has(ballkidId)) {
    return cachedAttendance.get(ballkidId);
  }

  try {
    const ref = docRef("attendance", ballkidId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      cachedAttendance.set(ballkidId, data);
      return data;
    }
    return { availability: {} };
  } catch (error) {
    console.error("Fehler beim Laden der Attendance-Daten:", error);
    return { availability: {} };
  }
};

export const saveAttendance = async (ballkidId, availability) => {
  await setDoc(docRef("attendance", ballkidId), { id: ballkidId, availability }, { merge: true });
  cachedAttendance.set(ballkidId, {
    ...(cachedAttendance.get(ballkidId) || {}),
    id: ballkidId,
    availability,
  });
};

export const getAvailableBallkidsForDay = async (day) => {
  const allMembers = await getMembers();
  await initAttendanceCache();

  return allMembers
    .filter((m) => {
      const attendance = cachedAttendance.get(m.id);
      const val = attendance?.availability?.[day];
      return val === true || (typeof val === "object" && val?.available);
    })
    .map((m) => {
      const attendance = cachedAttendance.get(m.id);
      const val = attendance?.availability?.[day];
      const from = typeof val === "object" && val?.from ? val.from : "";
      return { id: m.id, name: m.name, from, canNet: !!m.canNet };
    });
};

// ========================================
// Court Assignments
// ========================================
export const invalidateSchedulesCache = () => {
  schedulesCache = null;
  try { localStorage.removeItem(SCHEDULES_CACHE_KEY()); } catch (e) {}
};

export const markBallkidAsAssigned = async (ballkidId, day, court, crew) => {
  const courtKey = `${day}_${court}_${crew}`;
  const courtRef = docRef("courtAssignments", courtKey);

  try {
    const snap = await getDoc(courtRef);
    let assignedIds = snap.exists() ? snap.data().ballkids || [] : [];

    if (!assignedIds.includes(ballkidId)) {
      assignedIds.push(ballkidId);
      await setDoc(courtRef, { ballkids: assignedIds }, { merge: true });
    }
    invalidateSchedulesCache();
  } catch (error) {
    console.error("Fehler beim Zuweisen:", error);
  }
};

export const getAllCourtAssignments = async (day) => {
  const snapshot = await getDocs(col("courtAssignments"));
  const allMembers = await getMembers();
  const idToName = {};
  allMembers.forEach((m) => { idToName[m.id] = m.name; });

  let assignments = {};
  for (const docSnap of snapshot.docs) {
    if (docSnap.id.startsWith(day)) {
      const ids = docSnap.data().ballkids || [];
      assignments[docSnap.id] = ids.map((id) => idToName[id] || "Unbekannt");
    }
  }
  return assignments;
};

export const unassignBallkid = async (ballkidName, day, court, crew) => {
  try {
    const ballkidId = await getBallkidIdByName(ballkidName);
    if (!ballkidId) return;

    const assignmentId = `${day}_${court}_${crew}`;
    const ref = docRef("courtAssignments", assignmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;

    const updated = (snap.data().ballkids || []).filter((id) => id !== ballkidId);
    await updateDoc(ref, { ballkids: updated });
    invalidateSchedulesCache();
  } catch (error) {
    console.error("Fehler beim Entfernen:", error);
  }
};

export const getCourtAssignment = async (day, court) => {
  const ref = docRef("courtAssignments", `${day}_${court}`);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : { crewA: [], crewB: [], crewC: [] };
};

export const saveCourtAssignment = async (day, court, crew, assignments) => {
  const ref = docRef("courtAssignments", `${day}_${court}`);
  await setDoc(ref, { [crew]: assignments }, { merge: true });
};

export const updateBallkidJobCount = async (day, court, crew) => {
  const assignmentKey = `${day}_${court}_${crew}`;
  const ref = docRef("courtAssignments", assignmentKey);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const assignedIds = snap.data().ballkids || [];
  const allMembers = await getMembers();
  const memberMap = {};
  allMembers.forEach((m) => { memberMap[m.id] = m; });

  for (const id of assignedIds) {
    if (memberMap[id]) {
      const currentJobs = memberMap[id].jobs || 0;
      await updateDoc(docRef("members", id), { jobs: currentJobs + 1 });
    }
  }
  invalidateMembersCache();
};

export const getBallkidAssignments = async (ballkidId) => {
  const snapshot = await getDocs(col("courtAssignments"));
  const assignments = [];

  snapshot.forEach((d) => {
    const data = d.data();
    if (data.ballkids && data.ballkids.includes(ballkidId)) {
      const [day, court, crew] = d.id.split("_");
      assignments.push({
        day,
        court,
        crew: crew === "crewA" ? "A" : crew === "crewB" ? "B" : crew === "crewC" ? "C" : "D",
      });
    }
  });

  return assignments.sort((a, b) => parseInt(a.day) - parseInt(b.day));
};

// ========================================
// Oeffentliche Einteilungen
// ========================================
export const publishAssignments = async (assignments) => {
  try {
    await Promise.all(
      Object.entries(assignments).map(async ([memberId, assignmentList]) => {
        await setDoc(docRef("einteilungen_public", memberId), { assignments: assignmentList }, { merge: true });
      })
    );
  } catch (error) {
    console.error("Fehler beim Veroeffentlichen:", error);
  }
};

export const getPublicAssignments = async (ballkidId) => {
  try {
    const snap = await getDoc(docRef("einteilungen_public", ballkidId));
    return snap.exists() ? snap.data().assignments || [] : [];
  } catch (error) {
    console.error("Fehler beim Abrufen der Einteilungen:", error);
    return [];
  }
};

// ========================================
// Kommentare
// ========================================
export const addBallkidComment = async (ballkidId, comment) => {
  await updateDoc(docRef("members", ballkidId), { comments: arrayUnion(comment) });
  invalidateMembersCache();
};

export const getBallkidComments = async (ballkidId) => {
  try {
    const snap = await getDoc(docRef("members", ballkidId));
    return snap.exists() ? snap.data().comments || [] : [];
  } catch (error) {
    return [];
  }
};

export const getAllBallkidComments = async () => {
  try {
    const snapshot = await getDocs(col("members"));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    return [];
  }
};

// ========================================
// Personal Messages (individual info per ballkid)
// ========================================
export const setPersonalMessage = async (ballkidId, message) => {
  await updateDoc(docRef("members", ballkidId), { personalMessage: message || "" });
  invalidateMembersCache();
};

export const getPersonalMessage = async (ballkidId) => {
  try {
    const snap = await getDoc(docRef("members", ballkidId));
    return snap.exists() ? snap.data().personalMessage || "" : "";
  } catch (error) {
    return "";
  }
};

// ========================================
// Court Start Times
// ========================================
export const getCourtStartTime = async (day, court) => {
  try {
    const snap = await getDoc(docRef("court_start_times", `${day}_${court}`));
    if (snap.exists()) {
      const data = snap.data();
      return {
        startTime: data.startTime || "",
        hasThreeCrews: data.hasThreeCrews || false,
        isTurnusEnabled: data.isTurnusEnabled,
      };
    }
    return { startTime: "", hasThreeCrews: false };
  } catch (error) {
    return { startTime: "", hasThreeCrews: false };
  }
};

export const getCourtsFromStartTimes = async () => {
  try {
    const snapshot = await getDocs(col("court_start_times"));
    const courts = new Set();
    snapshot.docs.forEach((d) => {
      const court = d.id.split("_")[1];
      if (court) courts.add(court);
    });
    return Array.from(courts);
  } catch (error) {
    return [];
  }
};

export const setCourtStartTime = async (day, court, startTime, hasThreeCrews, isTurnusEnabled) => {
  await setDoc(docRef("court_start_times", `${day}_${court}`), { startTime, hasThreeCrews, isTurnusEnabled }, { merge: true });
};

// ========================================
// Check-In / Check-Out
// ========================================
export const checkInBallkid = async (ballkidId, day) => {
  try {
    const ref = docRef("check_in_out", `${day}_${ballkidId}`);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, { checkIn: new Date().toISOString(), checkOut: null });
    } else {
      await updateDoc(ref, { checkIn: new Date().toISOString(), checkOut: null });
    }
    invalidateCheckInCache();
  } catch (error) {
    console.error("Error checking in:", error);
  }
};

export const checkOutBallkid = async (ballkidId, day) => {
  try {
    const ref = docRef("check_in_out", `${day}_${ballkidId}`);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, { checkOut: new Date().toISOString() });
    }
    invalidateCheckInCache();
  } catch (error) {
    console.error("Error checking out:", error);
  }
};

// Shared check-in/out data loader with short-lived cache
const loadCheckInOutSnapshot = async () => {
  if (checkInOutCache && Date.now() - checkInOutCacheTimestamp < CHECK_IN_CACHE_TTL) {
    return checkInOutCache;
  }
  const snapshot = await getDocs(col("check_in_out"));
  checkInOutCache = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  checkInOutCacheTimestamp = Date.now();
  return checkInOutCache;
};

export const invalidateCheckInCache = () => {
  checkInOutCache = null;
  checkInOutCacheTimestamp = 0;
};

export const getCheckInStatus = async (day) => {
  try {
    const docs = await loadCheckInOutSnapshot();
    const status = {};
    const prefix = `${day}_`;
    docs.forEach((d) => {
      if (d.id.startsWith(prefix)) {
        const memberId = d.id.slice(prefix.length);
        status[memberId] = d.checkIn && !d.checkOut;
      }
    });
    return status;
  } catch (error) {
    return {};
  }
};

// Detailed check-in status with timestamps
export const getCheckInStatusDetailed = async (day) => {
  try {
    const docs = await loadCheckInOutSnapshot();
    const status = {};
    const prefix = `${day}_`;
    docs.forEach((d) => {
      if (d.id.startsWith(prefix)) {
        const memberId = d.id.slice(prefix.length);
        status[memberId] = {
          checkedIn: !!d.checkIn,
          checkedOut: !!d.checkOut,
          checkIn: d.checkIn || null,
          checkOut: d.checkOut || null,
        };
      }
    });
    return status;
  } catch (error) {
    return {};
  }
};

// Manual override for check-in/out times
export const updateCheckInOutTimes = async (ballkidId, day, checkIn, checkOut) => {
  try {
    const ref = docRef("check_in_out", `${day}_${ballkidId}`);
    const data = {};
    if (checkIn !== undefined) data.checkIn = checkIn;
    if (checkOut !== undefined) data.checkOut = checkOut;
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, data);
    } else {
      await setDoc(ref, { checkIn: checkIn || null, checkOut: checkOut || null });
    }
    invalidateCheckInCache();
  } catch (error) {
    console.error("Error updating check-in/out times:", error);
  }
};

// Bulk checkout for a crew on a court
export const checkOutCrew = async (day, court, crew) => {
  try {
    const assignmentKey = `${day}_${court}_${crew}`;
    const ref = docRef("courtAssignments", assignmentKey);
    const snap = await getDoc(ref);
    if (!snap.exists()) return [];

    const assignedIds = snap.data().ballkids || [];
    const now = new Date().toISOString();

    // Read all check-in docs in parallel
    const checkSnaps = await Promise.all(
      assignedIds.map((id) => getDoc(docRef("check_in_out", `${day}_${id}`)))
    );

    // Find which ones need checkout and write in parallel
    const toCheckOut = [];
    checkSnaps.forEach((checkSnap, i) => {
      if (checkSnap.exists() && checkSnap.data().checkIn && !checkSnap.data().checkOut) {
        toCheckOut.push(assignedIds[i]);
      }
    });

    await Promise.all(
      toCheckOut.map((id) => updateDoc(docRef("check_in_out", `${day}_${id}`), { checkOut: now }))
    );

    invalidateCheckInCache();
    return toCheckOut;
  } catch (error) {
    console.error("Error checking out crew:", error);
    return [];
  }
};

// Bulk checkout for all crews on a court
export const checkOutCourt = async (day, court) => {
  try {
    const results = [];
    for (const crew of ["crewA", "crewB", "crewC", "crewD"]) {
      const crewResults = await checkOutCrew(day, court, crew);
      results.push(...crewResults);
    }
    return results;
  } catch (error) {
    console.error("Error checking out court:", error);
    return [];
  }
};

export const getCheckInOutData = async () => {
  try {
    const docs = await loadCheckInOutSnapshot();
    return docs
      .filter((d) => d.checkIn)
      .map((d) => {
        const parts = d.id.split("_");
        const day = parts[0];
        const ballkidId = parts.slice(1).join("_");
        return { ballkidId, day, checkIn: d.checkIn, checkOut: d.checkOut || null };
      });
  } catch (error) {
    return [];
  }
};

// ========================================
// Essensmarken
// ========================================
export const getMealTicketStatus = async (day) => {
  try {
    const snap = await getDoc(docRef("essensmarken", day));
    return snap.exists() ? snap.data() : {};
  } catch (error) {
    return {};
  }
};

export const updateMealTicketStatus = async (day, ballkidId, status) => {
  try {
    const ref = docRef("essensmarken", day);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, { [ballkidId]: status });
    } else {
      await setDoc(ref, { [ballkidId]: status });
    }
  } catch (error) {
    console.error("Fehler beim Aktualisieren:", error);
  }
};

// ========================================
// Crew-Wechsel auf Court
// ========================================
export const getActiveCrew = async (day, court) => {
  try {
    const snap = await getDoc(docRef("crews_on_court", `${day}_${court}`));
    return snap.exists() ? snap.data() : { activeCrew: null, history: [] };
  } catch (error) {
    return { activeCrew: null, history: [] };
  }
};

export const setActiveCrew = async (day, court, crew, history) => {
  await setDoc(docRef("crews_on_court", `${day}_${court}`), { activeCrew: crew, history }, { merge: true });
};

// ========================================
// Attendance Comments
// ========================================
export const saveAttendanceComment = async (comment) => {
  await setDoc(docRef("attendance_comment", "comment"), { text: comment });
};

export const getAttendanceComment = async () => {
  try {
    const snap = await getDoc(docRef("attendance_comment", "comment"));
    return snap.exists() ? snap.data().text : "";
  } catch (error) {
    return "";
  }
};

// ========================================
// Einkleidung
// ========================================
export const getClothingStatus = async () => {
  const snapshot = await getDocs(col("einkleidung"));
  const status = {};
  snapshot.forEach((d) => { status[d.id] = d.data().received; });
  return status;
};

export const updateClothingStatus = async (ballkidId, received) => {
  await setDoc(docRef("einkleidung", ballkidId), { received });
};

// ========================================
// Daten-Export
// ========================================
const flattenData = (data) => {
  return data.map((item) => {
    const flattened = {};
    for (const key in item) {
      if (Array.isArray(item[key])) {
        flattened[key] = item[key].join(", ");
      } else if (typeof item[key] === "object" && item[key] !== null) {
        const flatObject = flattenData([item[key]]);
        for (const nestedKey in flatObject[0]) {
          flattened[`${key}_${nestedKey}`] = flatObject[0][nestedKey];
        }
      } else {
        flattened[key] = item[key];
      }
    }
    return flattened;
  });
};

export const exportAllData = async () => {
  const collectionNames = ["members", "freikarten", "check_in_out", "courtAssignments", "einkleidung", "essensmarken"];
  let allData = {};

  for (const name of collectionNames) {
    const snapshot = await getDocs(col(name));
    const rawData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    allData[name] = flattenData(rawData);
  }

  return allData;
};

// ========================================
// Eligibility Check
// ========================================
export const isBallkidEligible = async (ballkidId) => {
  try {
    const snap = await getDoc(docRef("einteilungen_public", ballkidId));
    return snap.exists();
  } catch (error) {
    return false;
  }
};

// ========================================
// Flexibles Poll-System
// ========================================

// Alle Polls laden (Admin)
export const getPolls = async () => {
  const snapshot = await getDocs(col("polls"));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Einzelnen Poll laden
export const getPoll = async (pollId) => {
  const snap = await getDoc(docRef("polls", pollId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Veroeffentlichte Polls laden (fuer BallkindPage)
export const getPublishedPolls = async () => {
  const snapshot = await getDocs(col("polls"));
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.published);
};

// Poll speichern/erstellen
export const savePoll = async (poll) => {
  const id = poll.id || doc(col("polls")).id;
  const { id: _, ...data } = poll;
  await setDoc(docRef("polls", id), { ...data, updatedAt: new Date().toISOString() });
  return id;
};

// Poll loeschen
export const deletePoll = async (pollId) => {
  const { deleteDoc: delDoc } = await import("firebase/firestore");
  await delDoc(docRef("polls", pollId));
  // Auch Votes loeschen
  const votesSnap = await getDocs(col(`poll_votes_${pollId}`));
  const deletions = votesSnap.docs.map((d) => delDoc(doc(db, `${currentGroupSlug}__poll_votes_${pollId}`, d.id)));
  await Promise.all(deletions);
};

// Vote abgeben
export const submitPollVote = async (pollId, memberId, vote) => {
  if (!memberId) return;
  await setDoc(
    doc(db, `${currentGroupSlug}__poll_votes_${pollId}`, memberId),
    { vote, timestamp: new Date().toISOString() }
  );
};

// Eigene Stimme laden
export const getPollVote = async (pollId, memberId) => {
  if (!memberId) return null;
  const snap = await getDoc(doc(db, `${currentGroupSlug}__poll_votes_${pollId}`, memberId));
  return snap.exists() ? snap.data().vote : null;
};

// Ergebnisse laden
export const getPollVoteSummary = async (pollId) => {
  const snapshot = await getDocs(collection(db, `${currentGroupSlug}__poll_votes_${pollId}`));
  const counts = {};
  snapshot.forEach((d) => {
    const vote = d.data().vote;
    if (vote) counts[vote] = (counts[vote] || 0) + 1;
  });
  return counts;
};

// Detaillierte Ergebnisse laden (wer hat was gewaehlt)
export const getPollVoteDetails = async (pollId) => {
  const snapshot = await getDocs(collection(db, `${currentGroupSlug}__poll_votes_${pollId}`));
  const allMembers = await getMembers();
  const memberMap = {};
  allMembers.forEach((m) => { memberMap[m.id] = m.name; });

  return snapshot.docs.map((d) => ({
    memberId: d.id,
    name: memberMap[d.id] || d.id,
    vote: d.data().vote,
    timestamp: d.data().timestamp,
  }));
};

// ========================================
// Ballkid Page Config
// ========================================
export const getBallkidPageConfig = async () => {
  try {
    const snap = await getDoc(docRef("page_config", "current"));
    return snap.exists() ? snap.data() : getDefaultPageConfig();
  } catch (error) {
    return getDefaultPageConfig();
  }
};

export const saveBallkidPageConfig = async (config) => {
  await setDoc(docRef("page_config", "current"), { ...config, updatedAt: new Date().toISOString() });
};

export const publishBallkidPageConfig = async (config) => {
  await setDoc(docRef("page_config", "published"), { ...config, publishedAt: new Date().toISOString() });
  await setDoc(docRef("page_config", "current"), { ...config, updatedAt: new Date().toISOString(), isPublished: true });
};

export const getPublishedPageConfig = async () => {
  try {
    const snap = await getDoc(docRef("page_config", "published"));
    return snap.exists() ? snap.data() : getDefaultPageConfig();
  } catch (error) {
    return getDefaultPageConfig();
  }
};

const getDefaultPageConfig = () => ({
  daysOfWeek: [
    "11. April", "12. April", "13. April", "14. April", "15. April",
    "16. April", "17. April", "18. April", "19. April",
  ],
  sliderEnabled: false,
  sliderText: "",
  sliderLink: "",
  sliderBgColor: "#ffca28",
  showAssignment: true,
  showCrewTimes: true,
  crewTimesLinkEnabled: true,
  crewTimesDay: "19. April",
  meetingPointEnabled: true,
  meetingPointText: "TREFFPUNKT FUER ALLE: 10:15 UHR!",
  atpLinkEnabled: true,
  atpLinkUrl: "https://www.atptour.com/en/scores/current/munich/308/daily-schedule",
  atpLinkText: "Zum offiziellen Spielplan (Order of Play)",
  customAnnouncementEnabled: false,
  customAnnouncement: "",
  showFreikarten: false,
  freikartenText: "",
  showImpressum: true,
  impressumUrl: "http://zilker-solutions.de/impressum-datenschutz-ballkid-management",
  customLinks: [],
  selfCheckInEnabled: false,
});

// ========================================
// Crew-Einsatzplaene (flexible Crew-Platz-Zuordnung)
// ========================================
export const getCrewSchedule = async (day) => {
  try {
    const snap = await getDoc(docRef("crew_schedules", day));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error("Error loading crew schedule:", error);
    return null;
  }
};

export const saveCrewSchedule = async (day, schedule) => {
  try {
    await setDoc(docRef("crew_schedules", day), {
      ...schedule,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error saving crew schedule:", error);
  }
};

export const getAllCrewSchedules = async () => {
  try {
    const snapshot = await getDocs(col("crew_schedules"));
    const schedules = {};
    snapshot.docs.forEach((d) => {
      schedules[d.id] = d.data();
    });
    return schedules;
  } catch (error) {
    console.error("Error loading crew schedules:", error);
    return {};
  }
};

// ========================================
// Turnier-Konfiguration (jetzt pro Gruppe in groups/{slug})
// ========================================
export const saveTournamentConfig = async (config) => {
  // Config wird jetzt im Gruppen-Doc gespeichert
  const groupRef = doc(db, "groups", currentGroupSlug);
  await setDoc(groupRef, { config }, { merge: true });
};

// ========================================
// Self Check-In (ID + Timestamp)
// ========================================
export const getCurrentMinuteSlot = () => Math.floor(Date.now() / 60000);

// ========================================
// Default-Export
// ========================================
const firebaseService = {
  getBallkids,
  getMembers,
  getAttendanceForBallkid,
  getAvailableBallkidsForDay,
  markBallkidAsAssigned,
  unassignBallkid,
  saveCourtAssignment,
};

export default firebaseService;
